import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { CoreService, UserActivityService } from '@kodit/core/core-ui';
import { AuthGuard, AuthService, MixpanelService } from '@kodit/core/services';
import { DynamicDialogConfig, DynamicDialogService } from '@kodit/core/shared-ui';
import {
  REFRESH_TOKEN_DIALOG_SHOW_SECONDS,
  REFRESH_TOKEN_REMAINING_TIME_STRING
} from 'libs/core/core-ui/src/lib/core-service';
import {
  RefreshTokenConfirmationDialogComponent
} from 'libs/core/core-ui/src/lib/ui/refresh-token-confirmation-dialog/refresh-token-confirmation-dialog.component';
import {
  NovaVerzijaForceDialogComponent
} from 'libs/core/core-ui/src/lib/ui/nova-verzija-force-dialog/nova-verzija-force-dialog.component';
import { DialogSize } from 'libs/core/shared-ui/src/lib/dialogs/dynamic-dialog/dynamicdialog-config';
import { ConfirmationService } from 'primeng/api';
import { interval, Subscription } from 'rxjs';

@Component({
  selector: 'ekompanija-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  /** Configurations */
  dialogConfig: DynamicDialogConfig = new DynamicDialogConfig(DialogSize.SMALL);
  isDialogOpened: boolean = false;
  timer: any;
  refreshWorker: Worker;
  dialogRef: any;
  /** Subs */
  private _subs: Subscription;

  constructor(
    private _mixpanelService: MixpanelService,
    public _authService: AuthService,
    private _coreService: CoreService,
    private _userActivityService: UserActivityService,
    private _dialogService: DynamicDialogService,
    private swUpdate: SwUpdate,
    private _confirmationService: ConfirmationService
  ) {
    this._subs = new Subscription();
  }

  ngOnInit(): void {
    this._mixpanelService.init();

    //this._handleRouteChanged();
    this._initRefreshWorker();
    this._subs.add(
      this._authService.getAppInitialized.subscribe((res) => {
        res ? this._startRefreshWorker() : this._clearTimer();
      })
    );

    this._subs.add(
      this._userActivityService.userActivity$.subscribe(() => {
        // Resetujemo tajmer na svaku akciju, ali ne dok je dijalog otvoren
        if (!this.isDialogOpened) {
          this._coreService.resetRemainingTimerSeconds();
        }
      })
    );

    if (this.swUpdate.isEnabled) {
      //Check every 15 mins(15min * 60sec * 1000ms)
      this._subs.add(
        interval(900000).subscribe(() => {
          this.swUpdate.checkForUpdate().then();
        })
      );

      this._subs.add(
        this.swUpdate.versionUpdates.subscribe((event) => {
          // if (event.type === 'VERSION_DETECTED') {
          //   window.confirm(
          //     'Nova verzija'
          //   );
          // }
          if (event.type === 'VERSION_READY') {
            const isForce = (event.latestVersion.appData as any)?.force === true;
            if (isForce) {
              this._showForceVersionUpdateDialog();
            } else {
              this._confirmationService.confirm({
                header: 'Nova verzija',
                message: 'Nova verzija aplikacije je dostupna.',
                acceptLabel: 'Osveži',
                acceptIcon: 'fa-solid fa-arrows-rotate',
                rejectVisible: false,
                accept: () => {
                  window.location.reload();
                },
              });
            }
          }
        })
      );
    }
  }

  ngOnDestroy() {
    this._clearTimer();
    this.isDialogOpened = false;
    this._subs.unsubscribe();
    this.refreshWorker.terminate();
    this._authService.setAppInitialized = false;
  }

  private _startRefreshWorker() {
    if (!this.refreshWorker) {
      this._initRefreshWorker();
    }
    this.refreshWorker.postMessage('hello');
  }

  private _initRefreshWorker() {
    if (typeof Worker !== 'undefined' && !this.refreshWorker) {
      this.refreshWorker = new Worker(new URL('./app.worker', import.meta.url));
      this.refreshWorker.onmessage = ({ data }) => {
        this._starRefreshTimer();
      };
    }
  }

  private _starRefreshTimer() {
    const tempRemainingTime = localStorage.getItem(
      REFRESH_TOKEN_REMAINING_TIME_STRING
    );

    if (!tempRemainingTime || Number.parseInt(tempRemainingTime) <= 0) {
      this._coreService.resetRemainingTimerSeconds();
    } else {
      this._coreService.setRemainingTimerSeconds = Number.parseInt(
        tempRemainingTime
      );
    }

    this.timer = setInterval(() => {
      this._coreService.setRemainingTimerSeconds =
        this._coreService.getRefreshTimerRemainingSecondsValue() - 1;

      this._coreService.setRemainingTimeToLocalStorage(
        this._coreService.getRefreshTimerRemainingSecondsValue()
      );

      if (
        this._coreService.getRefreshTimerRemainingSecondsValue() <=
        REFRESH_TOKEN_DIALOG_SHOW_SECONDS
      ) {
        if (!this.isDialogOpened) {
          this._showRefreshDialog();
          this.isDialogOpened = true;
        }
      }

      if (this._coreService.getRefreshTimerRemainingSecondsValue() <= 0) {
        this.dialogRef.close();
        this._authService.logout();
      }
    }, 1000);
  }

  private _showRefreshDialog() {
    this.dialogConfig.header = 'Upozorenje!';
    this.dialogConfig.closable = false;
    this.dialogConfig.customSubmitButton = { label: 'Nastavi rad' } as any;
    this.dialogConfig.customCancelButton = { label: 'Odjavi se' } as any;
    this.dialogConfig.maximisable = false;
    this.dialogConfig.closeOnEscape = false;

    this.dialogRef = this._dialogService.open(
      RefreshTokenConfirmationDialogComponent,
      this.dialogConfig
    );

    this._subs.add(
      this.dialogRef.onClose.subscribe((res) => {
        if (!res) {
          this._authService.logout();
        }

        this._clearTimer();
        this.isDialogOpened = false;
      })
    );
  }

  private _showForceVersionUpdateDialog() {
    const config = new DynamicDialogConfig(DialogSize.SMALL);
    config.header = 'Obavezno ažuriranje';
    config.closable = false;
    config.closeOnEscape = false;
    config.dismissableMask = false;
    config.maximisable = false;
    config.hideCancel = true;
    config.customSubmitButton = { label: 'Osveži', icon: 'fa-solid fa-arrows-rotate' } as any;

    this._dialogService.open(NovaVerzijaForceDialogComponent, config);
  }

  private _clearTimer() {
    clearInterval(this.timer);
  }
}
